/**
 * Générateur — « Nommer un calcul, distinguer sommes et produits, termes et facteurs. »
 * Attendu ID : 904 | Niveau : 5ème | Domaine : Nombres et calculs / Opérations
 *
 * Mis en conformité avec la spécification 904.json (campagne 5e, 2026-09-22).
 * La spécification est la seule référence ; ce qu'elle ne dit pas est noté dans
 * le rapport audit/5eme/904.md, section « Non tranché ».
 *
 * ─── Plan de cartes ────────────────────────────────────────────────────────
 *
 *   Carte « Comment s'appelle ce calcul ? » — nommer d'après l'opération de
 *     DERNIER RANG, celle qu'on effectuerait en dernier.
 *     Formats : QCM (liste fermée de quatre à six mots), classer, vrai/faux,
 *     QCM multiple. Niveaux 1 (une opération) et 3 (deux opérations).
 *
 *   Carte « Les termes et les facteurs » — désigner un terme d'une somme ou un
 *     facteur d'un produit, le nombre ou la SOUS-EXPRESSION ENTIÈRE.
 *     Formats : saisie (une opération, niveau 2), saisie (deux opérations dont
 *     un terme est lui-même un calcul, niveau 4).
 *
 *   Carte « Nommer puis désigner » — trois opérations et un niveau
 *     d'imbrication : nommer le calcul, puis en donner un terme ou un facteur.
 *     Format : texte à trous en saisie. Niveau 5.
 *
 *   Carte « Le vocabulaire du calcul » — le lexique en situation.
 *     Formats : texte à trous en glisser-déposer, relier. Niveaux 1 et 2.
 *
 *   Carte « Dis-le en français » — reconstituer la phrase qui décrit
 *     l'expression, mots de liaison compris.
 *     Format : texte à trous en glisser-déposer. Niveau 6.
 *
 * ─── Spécification (904.json) ──────────────────────────────────────────────
 *
 *   Vocabulaire : la liste IMPOSÉE (somme, produit, terme, facteur) et la
 *     liste complémentaire (différence, quotient). Rien d'autre : ni double,
 *     ni triple, ni tiers, ni carré, ni cube (exclusion de la spec).
 *   Bornes appliquées : 1 à 3 opérations ; 2 à 4 termes d'une somme ; 2 à 4
 *     facteurs d'un produit ; imbrication de parenthèses 0 à 2 ; opérandes de
 *     2 à 20 ; nombres affichés de 2 à 400 ; rang du terme ou du facteur
 *     demandé de 1 à 3 ; part des questions portant sur les termes et les
 *     facteurs : 5 formats sur 11, soit 0,45 (la spec demande 0,33 à 0,5).
 *   Exclusions appliquées : aucune expression au nom ambigu (l'opération de
 *     dernier rang est toujours unique) ; quotients exacts et différences
 *     positives ; aucun facteur égal à 1, aucun terme nul ; aucune expression
 *     à une seule opération quand la question porte sur le nom global d'un
 *     calcul à plusieurs opérations.
 *   Erreurs visées → mots-clés (`_meta.params.distracteurs[].code`, ou
 *     `_meta.params.erreurVisee` pour une erreur provoquée en saisie) :
 *     1. « Nommer d'après l'opération la plus visible »  → operation_la_plus_visible
 *     2. « Confondre terme et facteur »                  → terme_pour_facteur
 *     3. « Dénombrer les nombres au lieu des termes »     → denombrement_des_nombres
 *     4. « Appeler somme toute expression avec un + »     → plus_donc_somme
 *     5. « Désigner le nombre le plus proche au lieu de la sous-expression »
 *                                                         → nombre_au_lieu_de_la_sous_expression
 *     6. « Répondre par la valeur quand on demande le nom » → valeur_au_lieu_du_nom
 *   Graduation → cartes :
 *     niveau 1 → nommer (branche une opération), vocabulaire_relier ;
 *     niveau 2 → designer_texte, vocabulaire_texte_trou_drag ;
 *     niveau 3 → nommer (branche deux opérations), somme_ou_produit (3 formats) ;
 *     niveau 4 → designer_calcul_texte ;
 *     niveau 5 → nommer_designer_texte_trou_saisie ;
 *     niveau 6 → phrase_texte_trou_drag.
 *   Réglage annoncé dans la consigne (la spec impose de décider ET
 *   d'annoncer) : une sous-expression se recopie AVEC ses parenthèses, et le
 *   quotient s'écrit avec le signe de division.
 *
 * ─── Cartes retirées (hors attendu) ────────────────────────────────────────
 *   « L'aire du rectangle bleu » et « Deux expressions pour un salaire »
 *   (trois formats) : elles demandaient de produire ou de calculer des
 *   expressions équivalentes, ce qui relève de l'attendu 903 (traduire) et du
 *   calcul, pas du NOMMAGE. Elles venaient d'un cadrage antérieur à la
 *   spécification.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, makeRelier, makeClasser, makeSaisie,
    filtrerDistracteurs,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 904;

const L_NOM = 'Comment s\'appelle ce calcul ?';
const L_TF = 'Les termes et les facteurs';
const L_MIX = 'Nommer puis désigner';
const L_VOC = 'Le vocabulaire du calcul';
const L_PHR = 'Dis-le en français';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** Le vocabulaire de la spec, et rien d'autre. */
const NOMS = { '+': 'une somme', '−': 'une différence', '×': 'un produit', '÷': 'un quotient' };
const NOMS_TOUS = Object.values(NOMS);
/** Le mot qui désigne les opérandes, selon l'opération de tête. */
const MOT_OPERANDE = { '+': 'termes', '−': 'termes', '×': 'facteurs', '÷': 'facteurs' };

const avecParams = (payload, params) => {
    payload._meta.params = params;
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Le domaine : des expressions dont l'opération de DERNIER RANG est unique
// ═══════════════════════════════════════════════════════════

/**
 * Une expression à UNE opération : { txt, tete, gauche, droite }.
 * Quotients exacts, différences positives, aucun facteur 1 ni terme nul.
 */
const tirerUneOperation = () => {
    const tete = randomChoice(['+', '−', '×', '÷']);
    if (tete === '÷') {
        const b = randInt(2, 12);
        const q = randInt(2, 20);
        const a = b * q;                                  // quotient exact, a ≤ 240
        return { txt: `${a} ÷ ${b}`, tete, gauche: String(a), droite: String(b) };
    }
    if (tete === '−') {
        const b = randInt(2, 20);
        const a = b + randInt(2, 20);                     // différence positive
        return { txt: `${a} − ${b}`, tete, gauche: String(a), droite: String(b) };
    }
    const a = randInt(2, 20);
    const b = randInt(2, 20);
    return { txt: `${a} ${tete} ${b}`, tete, gauche: String(a), droite: String(b) };
};

/**
 * Une expression à DEUX opérations dont l'opération de dernier rang est
 * UNIQUE et identifiable : { txt, tete, gauche, droite, sousExpression }.
 * `sousExpression` est celui des deux opérandes qui est lui-même un calcul.
 */
const tirerDeuxOperations = () => {
    const a = randInt(2, 20);
    const b = randInt(2, 20);
    const c = randInt(2, 12);
    const forme = randomChoice(['somme_produit', 'produit_somme', 'produit_difference', 'difference_produit']);
    if (forme === 'somme_produit') {
        // a + b × c : la dernière opération est l'addition → une somme
        return { txt: `${a} + ${b} × ${c}`, tete: '+', gauche: String(a), droite: `${b} × ${c}`, sousExpression: `${b} × ${c}` };
    }
    if (forme === 'produit_somme') {
        // (a + b) × c : la dernière opération est la multiplication → un produit
        return { txt: `(${a} + ${b}) × ${c}`, tete: '×', gauche: `(${a} + ${b})`, droite: String(c), sousExpression: `(${a} + ${b})` };
    }
    // Différences : le retranché est tiré SOUS la valeur du premier opérande, jamais au-dessus.
    // Sans cela « (2 + 2) − 12 » sortait, et la spec exclut les différences négatives.
    if (forme === 'produit_difference') {
        const d = randInt(2, Math.max(2, Math.min(12, a + b - 2)));
        return { txt: `(${a} + ${b}) − ${d}`, tete: '−', gauche: `(${a} + ${b})`, droite: String(d), sousExpression: `(${a} + ${b})` };
    }
    const d = randInt(2, Math.max(2, Math.min(12, a * b - 2)));
    return { txt: `${a} × ${b} − ${d}`, tete: '−', gauche: `${a} × ${b}`, droite: String(d), sousExpression: `${a} × ${b}` };
};

/**
 * Une expression à TROIS opérations et un niveau d'imbrication, de tête unique.
 * Exemple : ((a + b) × c) − d, une différence dont le premier terme est un produit.
 */
const tirerTroisOperations = () => {
    const a = randInt(2, 15);
    const b = randInt(2, 15);
    const c = randInt(2, 9);
    // Le retranché reste sous (a + b) × c : la spec exclut les différences négatives.
    const d = randInt(2, Math.min(20, (a + b) * c - 2));
    if (randomChoice([true, false])) {
        // (a + b) × c + d : la dernière opération est l'addition → une somme
        return {
            txt: `(${a} + ${b}) × ${c} + ${d}`, tete: '+',
            gauche: `(${a} + ${b}) × ${c}`, droite: String(d),
            sousExpression: `(${a} + ${b}) × ${c}`,
        };
    }
    // ((a + b) × c) − d, avec (a + b) × c > d : une différence
    return {
        txt: `(${a} + ${b}) × ${c} − ${d}`, tete: '−',
        gauche: `(${a} + ${b}) × ${c}`, droite: String(d),
        sousExpression: `(${a} + ${b}) × ${c}`,
    };
};

// ═══════════════════════════════════════════════════════════
// Carte 1 — Comment s'appelle ce calcul ? (niveaux 1 et 3)
// ═══════════════════════════════════════════════════════════

const REGLE_DERNIERE =
    '<p><em>Le nom d\'un calcul vient de l\'opération qu\'on effectue en <strong>dernier</strong>.</em></p>';

const genNommerQcm = () => {
    const deux = randomChoice([true, false]);
    const e = deux ? tirerDeuxOperations() : tirerUneOperation();
    const correct = NOMS[e.tete];
    // Distracteurs : les autres noms d'opération (liste fermée de la spec). Sur une expression à
    // deux opérations, le nom de l'opération la plus VISIBLE est l'erreur n° 1 ; il est toujours
    // dans la liste puisque les quatre noms y figurent.
    const dis = NOMS_TOUS.filter((n) => n !== correct).map((n) => ({
        valeur: n,
        code: deux && n === NOMS[e.sousExpression && e.sousExpression.includes('×') ? '×' : '+']
            ? 'operation_la_plus_visible'
            : (n === 'une somme' ? 'plus_donc_somme' : 'terme_pour_facteur'),
    }));
    const texte = `<p>Voici une expression : <strong>${e.txt}</strong></p>`
        + '<p><strong>Comment s\'appelle ce calcul ?</strong></p>' + (deux ? REGLE_DERNIERE : '');
    const payload = makeQcm([correct, ...dis.map((d) => d.valeur)], correct, texte, opts(L_NOM));
    return avecParams(payload, {
        niveau: deux ? 3 : 1, expression: e.txt, tete: e.tete, operations: deux ? 2 : 1,
        distracteurs: dis,
    });
};

/** Trois expressions bâties sur les MÊMES nombres, de natures différentes. */
const trioMemesNombres = () => {
    const a = randInt(2, 15);
    const b = randInt(2, 9);
    const c = randInt(2, 9);
    return [
        { txt: `${a} + ${b} × ${c}`, nature: 'Une somme', tete: '+' },
        { txt: `(${a} + ${b}) × ${c}`, nature: 'Un produit', tete: '×' },
        { txt: `${a} × ${b} − ${c}`, nature: 'Une différence', tete: '−' },
    ];
};

/**
 * Deux trios dont les six expressions sont toutes DISTINCTES : deux tirages peuvent tomber sur les
 * mêmes nombres, et la colonne afficherait alors deux fois la même expression (le compte annoncé
 * « deux produits » deviendrait faux après déduplication). Repli déterministe borné.
 */
const deuxTrios = () => {
    for (let essai = 0; essai < 40; essai++) {
        const a = trioMemesNombres();
        const b = trioMemesNombres();
        const tous = [...a, ...b].map((e) => e.txt);
        if (new Set(tous).size === tous.length) return [...a, ...b];
    }
    return [
        { txt: '7 + 5 × 3', nature: 'Une somme', tete: '+' },
        { txt: '(7 + 5) × 3', nature: 'Un produit', tete: '×' },
        { txt: '7 × 5 − 3', nature: 'Une différence', tete: '−' },
        { txt: '9 + 4 × 6', nature: 'Une somme', tete: '+' },
        { txt: '(9 + 4) × 6', nature: 'Un produit', tete: '×' },
        { txt: '9 × 4 − 6', nature: 'Une différence', tete: '−' },
    ];
};

const genNommerClasser = () => {
    const exprs = deuxTrios();
    const natures = ['Une somme', 'Une différence', 'Un produit'];
    const categories = natures.map((nature) => ({
        name: nature,
        items: exprs.filter((e) => e.nature === nature).map((e) => e.txt),
    }));
    const texte = '<p>Classe chaque expression d\'après l\'opération qu\'on effectue en '
        + '<strong>dernier</strong>. Les nombres se ressemblent : seule la structure change.</p>';
    const payload = makeClasser(categories, texte, opts(L_NOM));
    return avecParams(payload, { niveau: 3, operations: 2, erreurVisee: 'operation_la_plus_visible' });
};

const genNommerVf = () => {
    const exprs = deuxTrios();
    const produits = shuffle(exprs.filter((e) => e.tete === '×')).slice(0, 2);
    const autres = shuffle(exprs.filter((e) => e.tete !== '×')).slice(0, 2);
    const lignes = shuffle([
        ...produits.map((e) => ({ txt: `${e.txt} est un produit.`, v: true })),
        ...autres.map((e) => ({ txt: `${e.txt} est un produit.`, v: false })),
    ]);
    const texte = '<p>Pour chaque expression, dis si l\'affirmation est vraie ou fausse.</p>' + REGLE_DERNIERE;
    const payload = makeVraiFaux(lignes.map((l) => l.txt), lignes.map((l) => (l.v ? 0 : 1)), texte, opts(L_NOM));
    return avecParams(payload, { niveau: 3, operations: 2, erreurVisee: 'plus_donc_somme' });
};

const genNommerQcmMulti = () => {
    const exprs = deuxTrios();
    const corrects = exprs.filter((e) => e.tete === '×').map((e) => e.txt);
    const texte = '<p>Coche les <strong>deux</strong> expressions qui sont des '
        + '<strong>produits</strong>.</p>' + REGLE_DERNIERE;
    const payload = makeQcm(shuffle(exprs.map((e) => e.txt)), corrects, texte, opts(L_NOM), true);
    return avecParams(payload, { niveau: 3, operations: 2, erreurVisee: 'operation_la_plus_visible' });
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — Les termes et les facteurs (niveaux 2 et 4)
// ═══════════════════════════════════════════════════════════

const REGLE_RECOPIE =
    '<p><em>Recopie le calcul entier s\'il y en a un, avec ses parenthèses s\'il en a.</em></p>';

const genDesignerSaisie = () => {
    const e = tirerUneOperation();
    const mot = MOT_OPERANDE[e.tete];
    const premier = randomChoice([true, false]);
    const attendu = premier ? e.gauche : e.droite;
    const rang = premier ? 'premier' : 'second';
    const texte = `<p>Voici ${NOMS[e.tete]} : <strong>${e.txt}</strong></p>`
        + `<p><strong>Quel est le ${rang} ${mot.replace(/s$/, '')} de ce calcul ?</strong> `
        + 'Écris seulement ce nombre.</p>';
    const payload = makeSaisie(attendu, texte, { ...opts(L_TF), comparaison: 'expression' });
    return avecParams(payload, {
        niveau: 2, expression: e.txt, tete: e.tete, operations: 1, rang: premier ? 1 : 2,
        reponse: attendu, erreurVisee: 'terme_pour_facteur',
    });
};

const genDesignerCalculSaisie = () => {
    const e = tirerDeuxOperations();
    const mot = MOT_OPERANDE[e.tete];
    // On demande TOUJOURS l'opérande qui est lui-même un calcul : c'est là que se joue
    // l'erreur n° 5, désigner le nombre le plus proche au lieu de la sous-expression entière.
    const estGauche = e.gauche === e.sousExpression;
    const rang = estGauche ? 'premier' : 'second';
    const texte = `<p>Voici ${NOMS[e.tete]} : <strong>${e.txt}</strong></p>`
        + `<p><strong>Quel est le ${rang} ${mot.replace(/s$/, '')} de ce calcul ?</strong></p>`
        + REGLE_RECOPIE;
    const payload = makeSaisie(e.sousExpression, texte, { ...opts(L_TF), comparaison: 'expression' });
    return avecParams(payload, {
        niveau: 4, expression: e.txt, tete: e.tete, operations: 2, rang: estGauche ? 1 : 2,
        reponse: e.sousExpression, erreurVisee: 'nombre_au_lieu_de_la_sous_expression',
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 3 — Nommer puis désigner (niveau 5)
// ═══════════════════════════════════════════════════════════

const genNommerDesignerTrous = () => {
    const e = tirerTroisOperations();
    const nom = NOMS[e.tete].replace(/^une |^un /, '');
    const mot = MOT_OPERANDE[e.tete];
    // « dont les termes sont {{A}} et {{B}} » : les deux trous étaient interchangeables
    // quand l'opération de tête est commutative, et la correction d'un texte à trous est
    // POSITIONNELLE — la phrase vraie « les termes sont B et A » était comptée fausse
    // (retour prof 2026-09-24). Les rangs « premier » et « second » lèvent l'ambiguïté,
    // avec le vocabulaire déjà employé par la carte designer_calcul_texte.
    const motSing = mot.replace(/s$/, '');
    const phrase = `${e.txt} est une {{${nom}}} dont le premier ${motSing} est `
        + `{{${e.sousExpression}}} et le second {{${e.droite}}}.`;
    const texte = `<p>Complète la phrase qui décrit l'expression <strong>${e.txt}</strong>.</p>`
        + REGLE_DERNIERE + REGLE_RECOPIE;
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_MIX), mode: 'saisie' });
    return avecParams(payload, {
        niveau: 5, expression: e.txt, tete: e.tete, operations: 3,
        reponse: [nom, e.sousExpression, e.droite],
        erreurVisee: 'nombre_au_lieu_de_la_sous_expression',
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 4 — Le vocabulaire du calcul (niveaux 1 et 2)
// ═══════════════════════════════════════════════════════════

const MOTS_VOCAB = ['somme', 'produit', 'termes', 'facteurs'];

const genVocabulaireTrouDrag = () => {
    const a = randInt(21, 199);
    const b = randInt(3, 9);
    const c = randInt(2, 9);
    const variante = randInt(0, 2);
    let phrase;
    if (variante === 0) {
        phrase = `${a} + ${b} × ${c} est une {{somme}} dont les {{termes}} sont ${a} et ${b} × ${c}.`;
    } else if (variante === 1) {
        phrase = `(${a} + ${b}) × ${c} est un {{produit}} dont les {{facteurs}} sont (${a} + ${b}) et ${c}.`;
    } else {
        phrase = `Dans le produit ${c} × (${a} + ${b}), les {{facteurs}} sont ${c} et (${a} + ${b}).`;
    }
    const distracteurs = filtrerDistracteurs(phrase, MOTS_VOCAB);
    const texte = '<p>Complète avec le bon vocabulaire :</p>';
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_VOC), mode: 'drag', distracteurs });
    return avecParams(payload, { niveau: 2, operations: 2, erreurVisee: 'terme_pour_facteur' });
};

const genVocabulaireRelier = () => {
    // b strictement plus petit que a : la même paire sert les quatre natures, et la spec exclut
    // les différences négatives (« 6 − 8 » sortait avant, constat au dump 2026-09-22).
    const b = randInt(2, 9);
    const a = b + randInt(2, 9);
    const pairs = shuffle([
        { source: `${a} + ${b}`, target: 'une somme' },
        { source: `${a} − ${b}`, target: 'une différence' },
        { source: `${a} × ${b}`, target: 'un produit' },
        { source: `${a * b} ÷ ${b}`, target: 'un quotient' },
    ]);
    const texte = '<p>Relie chaque expression à sa nature. '
        + 'Les nombres sont presque les mêmes partout : seule l\'opération change.</p>';
    const payload = makeRelier(pairs, texte, opts(L_VOC));
    return avecParams(payload, { niveau: 1, operations: 1, erreurVisee: 'valeur_au_lieu_du_nom' });
};

// ═══════════════════════════════════════════════════════════
// Carte 5 — Dis-le en français (niveau 6)
// ═══════════════════════════════════════════════════════════

/**
 * La phrase française qui décrit l'expression, MOTS DE LIAISON COMPRIS : c'est
 * ce que la spec demande au niveau 6. Les étiquettes non employées sont les
 * autres mots d'opération, comme le veut le point de vigilance de la spec.
 */
const genPhraseTrouDrag = () => {
    const e = tirerDeuxOperations();
    const nom = NOMS[e.tete].replace(/^une |^un /, '');
    const mot = MOT_OPERANDE[e.tete];
    const liaison = e.tete === '+' || e.tete === '−' ? 'et' : 'par';
    const phrase = e.tete === '×'
        ? `${e.txt} est le {{produit}} de ${e.gauche} {{par}} ${e.droite}.`
        : `${e.txt} est la {{${nom}}} de ${e.gauche} {{${liaison}}} ${e.droite}.`;
    const distracteurs = filtrerDistracteurs(phrase, ['somme', 'différence', 'produit', 'quotient', 'et', 'par']);
    const texte = `<p>Complète la phrase qui dit, en français, ce qu'est l'expression `
        + `<strong>${e.txt}</strong>.</p>` + REGLE_DERNIERE;
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_PHR), mode: 'drag', distracteurs });
    return avecParams(payload, {
        niveau: 6, expression: e.txt, tete: e.tete, operations: 2, mot,
        erreurVisee: 'operation_la_plus_visible',
    });
};

// ─── Registre et point d'entrée ──────────────────────────────

const GENERATORS = {
    nommer_qcm_unique: genNommerQcm,
    nommer_classer: genNommerClasser,
    nommer_tableau_vf: genNommerVf,
    nommer_qcm_multiple: genNommerQcmMulti,
    designer_texte: genDesignerSaisie,
    designer_calcul_texte: genDesignerCalculSaisie,
    nommer_designer_texte_trou_saisie: genNommerDesignerTrous,
    vocabulaire_texte_trou_drag: genVocabulaireTrouDrag,
    vocabulaire_relier: genVocabulaireRelier,
    phrase_texte_trou_drag: genPhraseTrouDrag,
};

export const CATEGORIES = [
    {
        id: 'nommer', label: L_NOM,
        formatsList: [
            { key: 'nommer_qcm_unique', label: 'QCM' },
            { key: 'nommer_classer', label: 'Classer' },
            { key: 'nommer_tableau_vf', label: 'Vrai/Faux' },
            { key: 'nommer_qcm_multiple', label: 'QCM Multi' },
        ],
    },
    {
        id: 'termes_facteurs', label: L_TF,
        formatsList: [
            { key: 'designer_texte', label: 'Saisie' },
            { key: 'designer_calcul_texte', label: 'Saisie (sous-expression)' },
        ],
    },
    {
        id: 'nommer_designer', label: L_MIX,
        formatsList: [
            { key: 'nommer_designer_texte_trou_saisie', label: 'Saisie trous' },
        ],
    },
    {
        id: 'vocabulaire', label: L_VOC,
        formatsList: [
            { key: 'vocabulaire_texte_trou_drag', label: 'Drag' },
            { key: 'vocabulaire_relier', label: 'Relier' },
        ],
    },
    {
        id: 'phrase', label: L_PHR,
        formatsList: [
            { key: 'phrase_texte_trou_drag', label: 'Drag' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

export const _internals = { tirerUneOperation, tirerDeuxOperations, tirerTroisOperations, trioMemesNombres };
